import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { PreSaleBanner } from '../components/layout/PreSaleBanner';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { SEO } from '../components/SEO';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Calendar, Share2 } from 'lucide-react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { ARTICLE_IMAGES } from '../data/articleImages';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogArticle() {
    const { slug } = useParams();
    const { t, lang } = useLanguage();
    const [article, setArticle] = useState(null);
    const [related, setRelated] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        setLoading(true);
        axios.get(`${API}/blog/articles/${slug}`)
            .then(res => { setArticle(res.data); setLoading(false); })
            .catch(() => { setError(true); setLoading(false); });
    }, [slug]);

    useEffect(() => {
        if (!article) return;
        axios.get(`${API}/blog/articles`)
            .then(res => {
                const others = (res.data || []).filter(a => a.slug !== slug);
                const score = a => (a.tags || []).filter(tg => (article.tags || []).includes(tg)).length;
                others.sort((a, b) => score(b) - score(a));
                setRelated(others.slice(0, 3));
            })
            .catch(() => {});
    }, [article, slug]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (error || !article) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <p className="text-muted-foreground mb-4">Article not found.</p>
                    <Link to="/blog" className="btn-secondary text-sm px-6 py-2.5">Back to blog</Link>
                </div>
            </div>
        );
    }

    const title = lang === 'nl' ? article.title_nl : article.title_en;
    const content = lang === 'nl' ? article.content_nl : article.content_en;
    const excerpt = lang === 'nl' ? article.excerpt_nl : article.excerpt_en;

    const articleJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: title,
        description: excerpt,
        image: ARTICLE_IMAGES[slug] ? [ARTICLE_IMAGES[slug]] : undefined,
        inLanguage: lang === 'nl' ? 'nl-NL' : 'en-GB',
        datePublished: article.published_at,
        dateModified: article.updated_at || article.published_at,
        keywords: (article.tags || []).join(', '),
        mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `https://sleepstack.nl/blog/${slug}`,
        },
        author: { '@type': 'Organization', name: 'Sleepstack' },
        publisher: {
            '@type': 'Organization',
            name: 'Sleepstack',
            logo: {
                '@type': 'ImageObject',
                url: 'https://sleepstack.nl/logo-mark.png',
            },
        },
    };

    return (
        <div>
            <SEO
                title={`${title} | Sleepstack`}
                description={excerpt}
                path={`/blog/${slug}`}
                type="article"
                jsonLd={articleJsonLd}
            />
            <PreSaleBanner />
            <Navbar />
            <main style={{ paddingTop: 'var(--page-offset, 128px)' }}>
                <article>
                    {/* Header */}
                    <header className="bg-white section-padding pb-12">
                        <div className="section-container max-w-3xl">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                            >
                                <Link
                                    to="/blog"
                                    className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8"
                                    data-testid="back-to-blog"
                                >
                                    <ArrowLeft size={15} />
                                    Back to blog
                                </Link>

                                {/* Tags */}
                                <div className="flex flex-wrap gap-2 mb-5">
                                    {article.tags?.map(tag => (
                                        <span key={tag} className="bg-primary/8 text-primary text-xs font-semibold px-3 py-1 rounded-full">
                                            {tag}
                                        </span>
                                    ))}
                                </div>

                                <h1
                                    className="heading-lg text-foreground mb-4 max-w-2xl"
                                    data-testid="article-title"
                                >
                                    {title}
                                </h1>

                                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                                    <span className="flex items-center gap-1.5">
                                        <Calendar size={13} />
                                        {new Date(article.published_at).toLocaleDateString(
                                            lang === 'nl' ? 'nl-NL' : 'en-GB',
                                            { day: 'numeric', month: 'long', year: 'numeric' }
                                        )}
                                    </span>
                                    <span className="flex items-center gap-1.5">
                                        <Clock size={13} />
                                        {article.read_time} min read
                                    </span>
                                    <button
                                        onClick={() => navigator.share?.({ title, url: window.location.href })}
                                        className="flex items-center gap-1.5 hover:text-primary transition-colors"
                                    >
                                        <Share2 size={13} /> Share
                                    </button>
                                </div>
                            </motion.div>
                        </div>
                    </header>

                    {/* Content */}
                    <div className="bg-accent pb-24">
                        <div className="section-container max-w-3xl">
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.5, delay: 0.2 }}
                                className="card-base p-8 md:p-12"
                            >
                                <div
                                    className="article-content"
                                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content || '') }}
                                    data-testid="article-content"
                                />
                            </motion.div>

                            {/* Related articles */}
                            {related.length > 0 && (
                                <div className="mt-16" data-testid="related-articles">
                                    <h2 className="font-heading text-2xl md:text-3xl text-foreground mb-8">{t('blog.related')}</h2>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        {related.map(a => (
                                            <Link
                                                key={a.slug}
                                                to={`/blog/${a.slug}`}
                                                className="card-base overflow-hidden group block"
                                                data-testid={`related-article-${a.id}`}
                                            >
                                                {ARTICLE_IMAGES[a.slug] && (
                                                    <div className="h-36 overflow-hidden">
                                                        <img
                                                            src={ARTICLE_IMAGES[a.slug]}
                                                            alt={lang === 'nl' ? a.title_nl : a.title_en}
                                                            loading="lazy"
                                                            decoding="async"
                                                            width="600"
                                                            height="400"
                                                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                        />
                                                    </div>
                                                )}
                                                <div className="p-5">
                                                    <h3 className="font-heading text-lg text-foreground leading-tight mb-2 group-hover:text-primary transition-colors">
                                                        {lang === 'nl' ? a.title_nl : a.title_en}
                                                    </h3>
                                                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                                        <Clock size={11} />
                                                        {a.read_time} {t('blog.minRead')}
                                                    </span>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </article>
            </main>
            <Footer />
        </div>
    );
}
